import React, { useLayoutEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import LunchDiningOutlinedIcon from "@mui/icons-material/LunchDiningOutlined";
import DeliveryDiningOutlinedIcon from "@mui/icons-material/DeliveryDiningOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import RoomServiceOutlinedIcon from "@mui/icons-material/RoomServiceOutlined";
import { dealCondition, dealHeadline, dealKind, dealNeedsCode } from "../../../utils/dealText";
import { BUTTON_RESET, C, FONT_BODY, FONT_DISPLAY, FOCUS_RING, RADIUS, TYPE } from "../../../utils/designTokens";

// Solid brand colour per kind of deal; rewards are ink with gold.
const LOOK = {
  discount: { bg: C.red, icon: LocalOfferOutlinedIcon },
  freeItem: { bg: C.teal, icon: LunchDiningOutlinedIcon },
  freeDelivery: { bg: C.teal, icon: DeliveryDiningOutlinedIcon },
  loyalty: { bg: C.ink, icon: WorkspacePremiumOutlinedIcon, gold: true },
  rewards: { bg: C.ink, icon: WorkspacePremiumOutlinedIcon, gold: true },
  // The Catering page's deals: green, with the cloche.
  catering: { bg: C.green, icon: RoomServiceOutlinedIcon, eyebrow: "#CDEBD7", big: 38 },
};

// In a row of deals each one takes the next of these, so no two side by side
// share a colour. The kind still picks the icon. All keep white text at 4.5:1.
const DEAL_COLOURS = [C.red, C.teal, C.green, "#A3470C", "#6A3D9A"];

// A 36px pill whose hit area reaches 44px.
const ctaBase = {
  ...BUTTON_RESET,
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
  height: 36,
  px: 1.75,
  borderRadius: RADIUS.pill,
  fontFamily: FONT_BODY,
  fontSize: 14,
  fontWeight: 700,
  whiteSpace: "nowrap",
  textDecoration: "none",
  "&::before": { content: '""', position: "absolute", inset: "-4px 0" },
};

function CopyCodePill({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard?.writeText(code);
    } catch (e) {
      // Clipboard blocked: the code is still on the pill to read.
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Box
      component="button"
      type="button"
      onClick={copy}
      aria-label={copied ? `Code ${code} copied` : `Copy code ${code}`}
      sx={{
        ...ctaBase,
        border: "1.5px dashed rgba(255,255,255,0.9)",
        bgcolor: "rgba(255,255,255,0.12)",
        color: "#FFFFFF",
        letterSpacing: "0.06em",
        "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
        "&:focus-visible": { outline: "2px solid #FFFFFF", outlineOffset: "2px" },
      }}
    >
      {code}
      {copied ? <CheckRoundedIcon sx={{ fontSize: 17 }} /> : <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />}
    </Box>
  );
}

/**
 * One banner: eyebrow, big headline, one line, a pill, and decoration.
 * `onOpen` makes the whole banner a button (the headline, stretched over it);
 * the pill stays clickable on top.
 */
export function DealBanner({ kind, bg, eyebrow, catering = false, headline, line, cta, onOpen, openLabel }) {
  const base = LOOK[kind] || LOOK.discount;
  const look = bg ? { ...base, bg } : base;
  const Icon = look.icon;
  return (
    <Box
      component="li"
      sx={{
        position: "relative",
        flex: "0 0 auto",
        width: { xs: "86%", sm: 380, lg: 400 },
        maxWidth: 420,
        height: 168,
        boxSizing: "border-box",
        p: "20px 24px",
        borderRadius: RADIUS.card,
        bgcolor: look.bg,
        color: "#FFFFFF",
        overflow: "hidden",
        scrollSnapAlign: "start",
        display: "flex",
        flexDirection: "column",
        listStyle: "none",
      }}
    >
      {/* Decoration: translucent circles and a large outline icon. */}
      <Box aria-hidden="true" sx={{ position: "absolute", width: 200, height: 200, borderRadius: "50%", right: -56, top: -64, bgcolor: look.gold ? "rgba(245,183,49,0.10)" : "rgba(255,255,255,0.10)" }} />
      <Box aria-hidden="true" sx={{ position: "absolute", width: 120, height: 120, borderRadius: "50%", right: 72, bottom: -72, bgcolor: look.gold ? "rgba(245,183,49,0.08)" : "rgba(255,255,255,0.08)" }} />
      <Icon aria-hidden="true" sx={{ position: "absolute", right: 18, top: "50%", transform: "translateY(-50%) rotate(-8deg)", fontSize: 104, color: look.gold ? "rgba(245,183,49,0.35)" : "rgba(255,255,255,0.28)" }} />

      {onOpen && (
        <Box
          component="button"
          type="button"
          onClick={onOpen}
          aria-label={openLabel}
          sx={{
            ...BUTTON_RESET,
            position: "absolute",
            inset: 0,
            zIndex: 1,
            borderRadius: RADIUS.card,
            "&:focus-visible": { outline: "2px solid #FFFFFF", outlineOffset: "-4px" },
          }}
        />
      )}

      {/* Above the whole-banner button; only the pill takes clicks itself. */}
      <Box sx={{ position: "relative", zIndex: 2, pointerEvents: onOpen ? "none" : "auto", display: "flex", flexDirection: "column", height: "100%" }}>
        {/* The eyebrow may run across the decoration; a catering deal says
            so in its own tag, so a long restaurant name can't push it out. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Box
            component="span"
            sx={{
              fontFamily: FONT_BODY,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: look.eyebrow || (look.gold ? C.goldAccent : "#FFFFFF"),
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
            }}
          >
            {eyebrow}
          </Box>
          {catering && (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                height: 22,
                px: 1,
                borderRadius: RADIUS.pill,
                bgcolor: "#FFFFFF",
                color: look.gold ? C.ink : look.bg,
                fontFamily: FONT_BODY,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              <RoomServiceRoundedIcon aria-hidden="true" sx={{ fontSize: 14 }} />
              Catering
            </Box>
          )}
        </Box>
        <Box
          component="h3"
          sx={{
            maxWidth: "68%",
            m: 0,
            mt: 0.5,
            fontFamily: FONT_DISPLAY,
            fontWeight: 800,
            fontSize: headline.length > 12 ? 26 : look.big || 34,
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
            color: look.gold ? C.goldAccent : "#FFFFFF",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {headline}
        </Box>
        {line && (
          <Box component="p" sx={{ ...TYPE.meta, fontSize: 14, maxWidth: "68%", m: 0, mt: 0.5, color: "#FFFFFF", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {line}
          </Box>
        )}
        <Box sx={{ mt: "auto", pt: 1, pointerEvents: "auto", alignSelf: "flex-start" }}>{cta}</Box>
      </Box>
    </Box>
  );
}

/**
 * "Deals for you": a sideways-scrolling row of large deal banners, with
 * previous / next buttons. `rewardsPoints` adds the customer's own rewards
 * banner after the restaurant's deals.
 */
function DealBanners({ promos = [], rewardsPoints, seeAllTo, eyebrowFor, onOpenPromo, title = "Deals for you", look: lookAll, sectionSx = { mt: { xs: 3, md: 4 } } }) {
  const scrollerRef = useRef(null);
  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (el) el.scrollBy({ left: dir * Math.max(320, el.clientWidth * 0.8), behavior: "smooth" });
  };

  let next = 0;
  const banners = promos.map((promo) => {
    const kind = lookAll || dealKind(promo);
    // A forced look (Catering) and the ink-and-gold loyalty deals keep their own colour.
    const bg = !lookAll && !LOOK[kind]?.gold ? DEAL_COLOURS[next++ % DEAL_COLOURS.length] : undefined;
    const look = bg ? { ...LOOK[kind], bg } : LOOK[kind];
    let cta;
    if (dealNeedsCode(promo)) {
      cta = <CopyCodePill code={promo.couponCode} />;
    } else if (kind === "loyalty") {
      cta = (
        <Box component="span" sx={{ ...ctaBase, cursor: "default", bgcolor: C.goldAccent, color: C.ink }}>
          Apply at checkout
        </Box>
      );
    } else {
      cta = (
        <Box component="span" sx={{ ...ctaBase, cursor: "default", bgcolor: "#FFFFFF", color: look.gold ? C.ink : look.bg }}>
          Apply at checkout
        </Box>
      );
    }
    return (
      <DealBanner
        key={promo._id}
        kind={kind}
        bg={bg}
        eyebrow={eyebrowFor ? eyebrowFor(promo) : promo.name}
        catering={Boolean(promo.isCatering) && lookAll !== "catering"}
        headline={dealHeadline(promo)}
        line={dealCondition(promo)}
        cta={cta}
        onOpen={onOpenPromo && promo.restaurantSlug ? () => onOpenPromo(promo) : undefined}
        openLabel={promo.restaurantName ? `${dealHeadline(promo)} at ${promo.restaurantName}. ${lookAll === "catering" ? "Open its catering menu" : "Open the restaurant"}` : undefined}
      />
    );
  });

  if (typeof rewardsPoints === "number") {
    banners.push(
      <DealBanner
        key="rewards"
        kind="rewards"
        eyebrow="Your rewards"
        headline={`${rewardsPoints.toLocaleString()} pts`}
        line="Redeem points for coupons"
        cta={
          <Box
            component={RouterLink}
            to="/rewards"
            sx={{
              ...ctaBase,
              bgcolor: C.goldAccent,
              color: C.ink,
              "&:hover": { bgcolor: "#E8A81F" },
              "&:focus-visible": { outline: `2px solid ${C.goldAccent}`, outlineOffset: "2px" },
            }}
          >
            See rewards
          </Box>
        }
      />
    );
  }

  // Start at the first banner whenever the set changes. The deals and the
  // rewards banner arrive separately, and with snap scrolling Chrome keeps
  // whichever banner was in view: rewards first, then deals put in front of
  // it, left the row scrolled to its end with the deals out of sight.
  const bannerKeys = banners.map((b) => b.key).join("|");
  useLayoutEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollLeft = 0;
  }, [bannerKeys]);

  if (!banners.length) return null;

  const arrow = (dir, label, Icon) => (
    <Box
      component="button"
      type="button"
      onClick={() => scrollBy(dir)}
      aria-label={label}
      sx={{
        ...BUTTON_RESET,
        width: 44,
        height: 44,
        borderRadius: "50%",
        border: `1px solid ${C.borderStrong}`,
        bgcolor: C.bg,
        color: C.ink,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        "&:hover": { bgcolor: C.surface },
        ...FOCUS_RING,
      }}
    >
      <Icon />
    </Box>
  );

  return (
    <Box component="section" aria-labelledby="deals-title" sx={sectionSx}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Box component="h2" id="deals-title" sx={{ ...TYPE.sectionTitle, m: 0, color: C.ink }}>
          {title}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {seeAllTo && (
            <Box
              component={RouterLink}
              to={seeAllTo}
              sx={{ ...FOCUS_RING, display: "inline-flex", alignItems: "center", minHeight: 40, px: 0.5, borderRadius: "8px", fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600, color: C.redLink, textDecoration: "none", whiteSpace: "nowrap", "&:hover": { textDecoration: "underline" } }}
            >
              See all deals
            </Box>
          )}
          {banners.length > 1 && (
            <Box sx={{ display: { xs: seeAllTo ? "none" : "flex", sm: "flex" }, gap: 1 }}>
              {arrow(-1, "Previous deals", ChevronLeftRoundedIcon)}
              {arrow(1, "Next deals", ChevronRightRoundedIcon)}
            </Box>
          )}
        </Box>
      </Box>
      <Box
        component="ul"
        ref={scrollerRef}
        sx={{
          display: "flex",
          gap: 2,
          m: 0,
          p: 0,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {banners}
      </Box>
    </Box>
  );
}

export default React.memo(DealBanners);
